/* eslint-disable @typescript-eslint/naming-convention */
import React, { useState, FormEvent, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Container,
  Grid,
  Paper,
  Box,
  MenuItem,
  TextField,
  Select,
  InputAdornment,
  IconButton,
  Button,
  SelectChangeEvent,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import Layout from "../Layout";
import { downloadRequest, fetchRequest } from "../../store/merchant/actions";
import { ApplicationState } from "../../store";
import PageHeading from "../../common/pageHeading/PageHeading";
import ProgressIndicator from "../../common/progressIndicator/ProgressIndicator";
import useSession from "../../effects/useSession";
import pageStyles from "../pageStyles";
// import { Status, useAbortableEffect } from "../../effects/useAbortableEffect";
import MerchantTable, {
  ColumnToRecordMapping,
  MerchantTableParams,
} from "./merchantTable/MerchantTable";
import { MRT_PaginationState } from "material-react-table";
import CloudDownload from "@mui/icons-material/CloudDownload";
import { IS_DMG_INSTANCE } from "../../lib/utils";
import { useEntityOrgConfig } from "../../common/OrgConfigProvider";
import { downloadLimit } from "../terminal/Terminal";
import _ from "lodash";
import CustomizedTooltip from "../../common/toolTip/CustomizedTooltip";

interface Pagination {
  page: {
    limit: string;
    offset: string;
  };
  filter: { [index: string]: string };
  sort: string;
  cache?: string;
}

export default function Merchant() {
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = pageStyles();
  const orgConfig = useEntityOrgConfig("merchant");
  const hiddenSearchFields = new Set(orgConfig.hiddenSearchFields);

  const urlSearchParams = new URLSearchParams(history.location.search);
  const onboardingId = urlSearchParams.get('onboardingId') as string;
  let initParams = new MerchantTableParams();

  const [params, setParams] = useState<MerchantTableParams>(
    onboardingId ? {
      ...initParams,
      searchField: 'OnboardingID',
      searchText: onboardingId
    } :  initParams
  );
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [isDownloadAvailable, setIsDownloadAvailable] = useState(false);
  const session = useSession();

  const handleOnChange = (event: { target: { value: string } }) => {
    setParams({
      ...params,
      searchText: event.target.value,
      cache: 'ignore'
    });
  };

  const refreshData = (newParams: MerchantTableParams) => {
    const p: Pagination = {
      page: {
        limit: String(newParams.pageSize),
        offset: String(newParams.pageSize * newParams.page),
      },
      filter: {},
      sort: "",
      cache: newParams.cache
    };

    if (newParams.orderBy > -1)
      p.sort =
        (newParams.orderDirection == "desc" ? "-" : "") +
        ColumnToRecordMapping[newParams.orderBy];
    if (newParams.searchText) {
      p.filter[newParams.searchField] = (newParams.searchText || "").trim();
    }
    setParams({ ...newParams });

    dispatch(fetchRequest(null, p));
  };

  const { loading, data } = useSelector(
    (state: ApplicationState) => state.merchant
  );

  const merchantsStore = data?.data ?? [];
  const merchantsStoreObjects = data?.meta?.totalObjects ?? 0;

  const onChangePage = (page: number, pageSize: number) => {
    const p = { ...params, pageSize: pageSize, page: page };
    refreshData(p);
  };

  const onSearchChange = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const p = { ...params, page: 0, cache: 'ignore' };
    refreshData(p);
  };

  useEffect(() => {
    onChangePage(pagination.pageIndex, pagination.pageSize);
  }, [pagination.pageIndex, pagination.pageSize]);

  useEffect(()=>{
    if(params.searchText !== '' && merchantsStoreObjects > 0 && merchantsStoreObjects <= downloadLimit) {
      setIsDownloadAvailable(true);
    }else {
      setIsDownloadAvailable(false);
    }
  },[merchantsStoreObjects]);

  const handleDownload = () => {
    const p = {
      page: {
        limit: '99999',
        offset: '0',
      },
      filter: {},
      action:"export",
      cache:"ignore",
      header:["MID", "Name", "OnboardingID","OnboardedDate"],
      sort: "",
    };
    p.filter[params.searchField] = (params.searchText || "").trim();
    dispatch(downloadRequest( p));
  };

  const downloadButtonTooltip = (storeObjects: number, searchText) => {
    if (_.isEmpty(searchText)) {
      return `Search data to use this feature`;
    } else {
      if (storeObjects === 0) {
        return "You have no item to download";
      } else if (storeObjects > downloadLimit) {
        return `You can download max ${downloadLimit} items while you have ${storeObjects} items in filtered data`;
      } else {
        return `Download for ${storeObjects} items below`;
      }
    }
  }

  return (
    <Layout sessionData={session}>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        <Container maxWidth="lg" className={classes.container}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper>
                <PageHeading
                  entity="Merchant"
                  heading="Merchants"
                  primaryButtonText="Add new merchant"
                  primaryButtonClickHandler={() =>
                    history.push("/merchant/new")
                  }
                />
              </Paper>
              <Box my={2}>
                <Paper>
                  <Box
                    display={"flex"}
                    justifyContent={"space-between"}
                    pb={2}
                    pr={2}
                  >
                    <div></div>
                    <form
                      onSubmit={(e) => onSearchChange(e)}
                      style={{ padding: "1rem" }}
                    >
                      {IS_DMG_INSTANCE &&
                        <CustomizedTooltip
                          title={downloadButtonTooltip(
                            merchantsStoreObjects,
                            params.searchText
                          )}
                        >
                          <Box component="span" marginRight={1}>
                            {/* <Button
                              variant="outlined"
                              size="small"
                              onClick={handleDownload}
                              disabled={!isDownloadAvailable}
                              startIcon={<CloudDownload />}
                            >Download</Button> */}
                          </Box>
                        </CustomizedTooltip>
                      }
                      <Select
                        variant="standard"
                        value={params.searchField}
                        onChange={(event: SelectChangeEvent<string>) => {
                          setParams({
                            ...params,
                            searchField: event.target.value,
                          });
                        }}
                      >
                        <MenuItem value="attributes.Name" selected={true}>
                          Merchant Name
                        </MenuItem>
                        {!hiddenSearchFields.has("attributes.MID") && <MenuItem value="attributes.MID" >Merchant ID</MenuItem>}

                        {!hiddenSearchFields.has("OnboardingID") && <MenuItem value="OnboardingID" >Onboarding Request ID</MenuItem>}
                      </Select>
                      <TextField
                        style={{  marginLeft: 5, minWidth: "200px", flex: 1 }}
                        placeholder={"Search"}
                        variant="standard"
                        onChange={handleOnChange}
                        value={params.searchText}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton type="submit" edge="end">
                                <Search />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />

                    </form>
                  </Box>

                  {loading ? (
                    <ProgressIndicator />
                  ) : (
                    <MerchantTable
                      merchant={merchantsStore}
                      merchantCount={merchantsStoreObjects}
                      setPagination={setPagination}
                      pagination={pagination}
                    />
                  )}
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </main>
    </Layout>
  );
}
